import { appOrigin } from "@/lib/absolute-url";
import { buildLocalizedPagePath } from "@/lib/localized-page-path";
import { buildFeaturePageLandingPath } from "@garimpa/lib/buildFeaturePageLandingPath";
import { buildFeaturePagePath } from "@garimpa/lib/buildFeaturePagePath";
import { type FeaturePageFamily } from "@garimpa/lib/featurePage";
import {
  DEFAULT_VIEWER_LOCALE,
  VIEWER_LOCALES,
  type ViewerLocale,
} from "@garimpa/lib/viewerPreferences";

/**
 * Shape mirroring Next's `Metadata["alternates"]` subset — kept simple so
 * TanStack `head()` can emit canonical + hreflang <link> tags without
 * depending on `next-intl/server`'s async context.
 */
export interface AlternatesBlock {
  canonical: string;
  languages: Record<string, string>;
}

function absoluteUrl(path: string): string {
  const origin = appOrigin();
  return path === "/" ? origin : `${origin}${path}`;
}

export function buildCanonicalAlternates(path: string): AlternatesBlock {
  return { canonical: absoluteUrl(path), languages: {} };
}

export function buildLocalizedPageAlternates(
  current: ViewerLocale,
  ...segments: string[]
): AlternatesBlock {
  const languages = Object.fromEntries(
    VIEWER_LOCALES.map((locale) => [
      locale,
      absoluteUrl(buildLocalizedPagePath(locale, ...segments)),
    ]),
  );
  languages["x-default"] = languages[DEFAULT_VIEWER_LOCALE]!;

  return {
    canonical: absoluteUrl(buildLocalizedPagePath(current, ...segments)),
    languages,
  };
}

export function buildFeaturePageAlternates(input: {
  family: FeaturePageFamily;
  slug?: string;
  current: ViewerLocale;
  availableLocales: readonly ViewerLocale[];
}): AlternatesBlock {
  const languages: Record<string, string> = {};
  for (const locale of input.availableLocales) {
    const path = input.slug
      ? buildFeaturePagePath(locale, input.family, input.slug)
      : buildFeaturePageLandingPath(locale, input.family);
    languages[locale] = absoluteUrl(path);
  }
  if (input.availableLocales.includes(DEFAULT_VIEWER_LOCALE)) {
    languages["x-default"] = languages[DEFAULT_VIEWER_LOCALE]!;
  }
  const canonicalPath = input.slug
    ? buildFeaturePagePath(input.current, input.family, input.slug)
    : buildFeaturePageLandingPath(input.current, input.family);
  return {
    canonical: absoluteUrl(canonicalPath),
    languages,
  };
}

/**
 * Converts an AlternatesBlock into the `{ rel, hrefLang?, href }[]` shape
 * TanStack `head().links` expects.
 */
export function alternatesToLinks(alts: AlternatesBlock) {
  return [
    { rel: "canonical", href: alts.canonical },
    ...Object.entries(alts.languages).map(([hrefLang, href]) => ({
      rel: "alternate",
      hrefLang,
      href,
    })),
  ];
}
